/* CHOOSING A PASSWORD, in the login page's own clothes.

   Max, 2026-10-07: "sửa lại cái screen này nha xấu quá". It was the portal's oldest form — a
   1000px white slab with label-beside-input rows, a black pill button and a Font Awesome
   stylesheet it never used — sitting at the end of a mail whose own design was rebuilt this week.

   IT IS THE LOGIN PAGE AS FAR AS THE PERSON IS CONCERNED. They arrive here from a mail, before
   any session exists, and go straight to /login afterwards; a different-looking page in the
   middle of that reads as a different site. So it wears the same chrome as Login and Request
   access: the week's photograph, the glass card, the wordmark on the picture.

   "CHOOSE YOUR PASSWORD", NOT "RESET PASSWORD". One URL serves two arrivals — a brand-new account
   whose mail button says "Set your password", and somebody who forgot theirs — and the old title
   was wrong for the first of them, which is the one this week's work created. Choosing covers
   both without claiming something of theirs was changed. */
import "./style.css";
import React, { useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import PasswordField from './PasswordField';
import logo from "./Helinox- _White.png";
import { backgroundForThisWeek } from "./loginBackgrounds";
// Same origin, like every other screen: nginx proxies /api and /auth to the backend on both
// ports, and an absolute http:// URL is blocked as mixed content the moment the site answers
// on https (2026-09-02). See backendConfig.js.
import { BACKEND_URL } from './backendConfig';

// Dictionary keys (src/locales/xAuth.js): the label over the box, and the word the eye's tooltip names.
const FIELDS = [
    { k: 'newPassword', labelKey: 'xAuth.newPassword', whatKey: 'xAuth.whatNewPassword' },
    { k: 'confirmPassword', labelKey: 'xAuth.confirmPassword', whatKey: 'xAuth.whatConfirmation' },
];

function ResetPassword() {
    const { t } = useTranslation();
    const [values, setValues] = useState({ newPassword: '', confirmPassword: '' });
    const [message, setMessage] = useState({ text: '', type: '' });
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const token = new URLSearchParams(location.search).get('token');
    const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

    const validateFields = () => {
        const out = [];
        const { newPassword, confirmPassword } = values;
        if (!newPassword || !confirmPassword) out.push(t('xAuth.valAllFields'));
        if (newPassword.length < 8) out.push(t('xAuth.valPwLength'));
        if (!/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/.test(newPassword)) {
            out.push(t('xAuth.valPwMix'));
        }
        if (newPassword !== confirmPassword) out.push(t('xAuth.pwMismatch'));
        return out;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const found = validateFields();
        if (found.length) {
            setErrors(found);
            setLoading(false);
            return;
        }
        setErrors([]);
        try {
            const res = await axios.post(`${BACKEND_URL}/reset-password`,
                { token, newPassword: values.newPassword });
            setMessage({ text: res.data.message, type: 'success' });
            setTimeout(() => navigate('/login'), 3000);
        } catch (error) {
            setMessage({ text: error.response?.data?.error || t('xAuth.errorOccurred'), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    /* A LINK WITH NO TOKEN CANNOT BE ANSWERED, and saying so here beats letting somebody type a
       password twice and then be told. It happens: a mail client that wraps the URL, or the page
       opened from history. */
    if (!token) {
        return (
            <div className="login-page" style={{ backgroundImage: `url(${backgroundForThisWeek()})` }}>
                <header className="login-topbar">
                    <div className="login-topbar-brand">
                        <img src={logo} alt="Helinox" className="login-logo" />
                        <span>HEVN PORTAL</span>
                    </div>
                </header>
                <main className="login-card login-card-request">
                    <span className="login-mark login-mark-icon" aria-hidden="true">
                        <KeyRound size={36} strokeWidth={1.5} />
                    </span>
                    <h1 className="login-title">{t('xAuth.linkIncomplete')}</h1>
                    <p className="login-subtitle">
                        {t('xAuth.linkIncompleteSub')}
                    </p>
                    <p className="login-card-foot">
                        <Link to="/reset-password-request">{t('xAuth.sendNewLink')}</Link>
                    </p>
                </main>
            </div>
        );
    }

    return (
        <div className="login-page" style={{ backgroundImage: `url(${backgroundForThisWeek()})` }}>
            <header className="login-topbar">
                <div className="login-topbar-brand">
                    <img src={logo} alt="Helinox" className="login-logo" />
                    <span>HEVN PORTAL</span>
                </div>
            </header>

            <main className="login-card login-card-request">
                <span className="login-mark login-mark-icon" aria-hidden="true">
                    <KeyRound size={36} strokeWidth={1.5} />
                </span>
                <h1 className="login-title">{t('xAuth.choosePwTitle')}</h1>
                {message.type === 'success' ? (
                    <>
                        <p className="login-subtitle">{message.text}</p>
                        <p className="login-card-foot">
                            {t('xAuth.takingYou')} <Link to="/login">{t('xAuth.goNow')}</Link>
                        </p>
                    </>
                ) : (
                    <>
                        {/* THE RULES BEFORE THE TYPING, not after it. They used to sit under both
                            boxes as grey prose and were repeated as errors once the form was
                            rejected — the same two sentences twice, the second time in red. */}
                        <p className="login-subtitle">
                            {t('xAuth.pwRuleHint')}
                        </p>
                        <div className="login-card-body">
                            <form className="login-form" onSubmit={handleSubmit}>
                                {FIELDS.map((f) => (
                                    <div className="login-field-group" key={f.k}>
                                        <span className="login-field-label" id={`${f.k}-label`}>
                                            {t(f.labelKey)}
                                            <span className="login-req" aria-hidden="true">*</span>
                                        </span>
                                        <PasswordField
                                            id={f.k}
                                            labelId={`${f.k}-label`}
                                            what={t(f.whatKey)}
                                            value={values[f.k]}
                                            onChange={(e) => set(f.k, e.target.value)}
                                            required
                                        />
                                    </div>
                                ))}
                                {errors.length > 0 && (
                                    <div className="login-error-message">
                                        {errors.map((er) => <div key={er}>{er}</div>)}
                                    </div>
                                )}
                                {message.type === 'error' && (
                                    <div className="login-error-message">{message.text}</div>
                                )}
                                <button type="submit" className="login-submit-button" disabled={loading}>
                                    {loading ? t('xAuth.saving') : t('xAuth.savePassword')}
                                </button>
                            </form>
                        </div>
                        <p className="login-card-foot">
                            <Link to="/login">{t('xAuth.backToSignIn')}</Link>
                        </p>
                    </>
                )}
            </main>
        </div>
    );
}

export default ResetPassword;
